<script setup>
import { computed, inject } from 'vue';
import SettingsSection from '@/components/settings/SettingsSection.vue';
import UiAuthBrandPanel from '@/components/ui/UiAuthBrandPanel.vue';
import UiFormField from '@/components/ui/UiFormField.vue';
import { useBranding } from '@/branding';
import { useBrandAssets } from '@/composables/useBrandAssets';
import { SCHOOL_SETTINGS_KEY } from '@/composables/useSchoolSettingsForm';

// The brand panel on the sign-in and sign-up pages. Text options save with
// the Save button; the photo applies as soon as it's uploaded. The preview
// shows unsaved changes.
const KEYS = ['loginHeadline', 'loginOverlay', 'loginShowFeatures'];
const PHOTO = 'login-background';

const { form, saving, savedAt, isDirty, reset, save } = inject(SCHOOL_SETTINGS_KEY);
const branding = useBranding();
const { busy, upload, reset: removeAsset } = useBrandAssets();

const photoUrl = computed(() => {
  const bg = branding.assets[PHOTO];
  return bg?.custom ? bg.url : '';
});

let input = null;
function onFile(event) {
  const file = event.target.files?.[0];
  event.target.value = '';
  upload(PHOTO, file);
}
</script>

<template>
  <SettingsSection
    title="Sign-in page"
    description="The panel beside the sign-in and sign-up forms."
    :dirty="isDirty(KEYS)"
    :saving="saving === 'login'"
    :saved-at="savedAt.login || 0"
    @save="save('login', KEYS)"
    @reset="reset(KEYS)"
  >
    <div class="login-settings">
      <div class="login-settings__form form-stack">
        <UiFormField
          label="Background photo"
          description="Optional landscape photo, e.g. the campus. Avoid photos with text or logos. Applies as soon as you upload."
        >
          <div class="photo">
            <div class="photo__thumb">
              <img v-if="photoUrl" :src="photoUrl" alt="Sign-in background" />
              <v-icon v-else size="20">mdi-image-outline</v-icon>
            </div>
            <div class="photo__actions">
              <v-btn
                variant="outlined"
                size="small"
                prepend-icon="mdi-upload-outline"
                :loading="busy === PHOTO"
                @click="input?.click()"
              >
                {{ photoUrl ? 'Replace' : 'Upload' }}
              </v-btn>
              <v-btn
                v-if="photoUrl"
                variant="text"
                size="small"
                :disabled="busy === PHOTO"
                @click="removeAsset(PHOTO, { optional: true })"
              >
                Remove
              </v-btn>
              <input
                :ref="(el) => (input = el)"
                type="file"
                accept="image/png,image/jpeg,image/webp"
                hidden
                @change="onFile"
              />
            </div>
          </div>
        </UiFormField>

        <UiFormField
          label="Photo overlay"
          :description="photoUrl ? `${form.loginOverlay}% darker, so the text stays readable.` : 'Used once a background photo is uploaded.'"
        >
          <v-slider
            v-model="form.loginOverlay"
            :min="0"
            :max="90"
            :step="5"
            :disabled="!photoUrl"
            color="primary"
            track-color="var(--border-strong)"
            thumb-label
            hide-details
          />
        </UiFormField>

        <UiFormField
          label="Headline"
          description="Leave empty for “Everything your school needs, in one place.”"
          v-slot="{ id }"
        >
          <v-text-field
            :id="id"
            v-model="form.loginHeadline"
            maxlength="150"
            counter
            placeholder="Everything your school needs, in one place."
            hide-details="auto"
          />
        </UiFormField>

        <v-switch
          v-model="form.loginShowFeatures"
          color="primary"
          label="Show what the system does (class records, attendance, parents, updates)"
          inset
          density="compact"
          hide-details
        />

        <p class="login-settings__note">
          The logo, school name, tagline and address come from General and DepEd details.
        </p>
      </div>

      <div class="login-settings__preview">
        <span class="login-settings__preview-label">Preview</span>
        <UiAuthBrandPanel
          compact
          class="login-settings__panel"
          :headline="form.loginHeadline"
          :overlay="Number(form.loginOverlay)"
          :show-features="!!form.loginShowFeatures"
          :background-url="photoUrl"
        />
      </div>
    </div>
  </SettingsSection>
</template>

<style scoped>
.login-settings {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(260px, 340px);
  gap: 24px;
  align-items: start;
}
.photo {
  display: flex;
  align-items: center;
  gap: 12px;
}
.photo__thumb {
  display: flex;
  flex: 0 0 96px;
  align-items: center;
  justify-content: center;
  height: 64px;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--subtle);
  color: var(--faint-foreground);
}
.photo__thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.photo__actions {
  display: flex;
  gap: 8px;
}
.login-settings__note {
  margin: 0;
  font-size: 13px;
  color: var(--muted-foreground);
}
.login-settings__preview {
  position: sticky;
  top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.login-settings__preview-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--muted-foreground);
}
.login-settings__panel {
  min-height: 440px;
  border-radius: 12px;
}
@media (max-width: 1100px) {
  .login-settings {
    grid-template-columns: 1fr;
  }
  .login-settings__preview {
    position: static;
    max-width: 380px;
  }
}
</style>
